import React from 'react';
import Eyebrow from '../motion/Eyebrow';
import KineticHeadline from '../motion/KineticHeadline';
import Reveal from '../motion/Reveal';
import LoopVideo from '../motion/LoopVideo';
import Tilt from '../motion/Tilt';
import Parallax from '../motion/Parallax';
import Claws from '../motion/Claws';
import SectionLight from '../motion/SectionLight';

/** CORNER CHAT: big feathered chat loop bleeding off the left, copy right. */
export function ChatSection() {
  return (
    <section className="pnp-section" aria-labelledby="pnp-chat-title">
      <SectionLight bleed="80px">
        <div className="pnp-glow" style={{ left: '-30%', top: '10%', width: 1100, height: 1100 }} />
      </SectionLight>
      <div className="pnp-wrap pnp-split">
        <Reveal className="pnp-split-media pnp-scroll-scale" rise={false}>
          <LoopVideo name="feature-chat" feather="ui" label="Corner Chat: a pool's live group chat during a UFC card" />
          <Claws className="pnp-split-claws" style={{ '--claw-w': '7px', '--claw-gap': '18px', right: '6%', top: '4%', height: '70%', opacity: 0.6 }} />
        </Reveal>
        <div className="pnp-split-copy">
          <Eyebrow>Corner Chat</Eyebrow>
          <div id="pnp-chat-title"><KineticHeadline text={'Trash talk.\n*Every round.*'} /></div>
          <Reveal delay={200}>
            <p className="pnp-lead">
              Every pool gets its own Corner Chat, a live group chat that sits right next to your picks. Call the upset
              before the walkouts, then collect the receipts when the decision is read.
            </p>
          </Reveal>
          <Reveal delay={320} className="pnp-chips">
            {['One chat per pool', 'Messages land live', 'Unread counts', 'Just your pool'].map((c) => (
              <span key={c} className="pnp-chip"><span className="pnp-chip-dot" aria-hidden="true" />{c}</span>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const RULES = [
  {
    title: 'Pick the fights',
    body: 'Run the main card only, main card plus prelims, or every fight from the early prelims up.',
    opts: [{ t: 'Main card' }, { t: '+ Prelims' }, { t: 'Full card', hot: true }],
  },
  {
    title: 'Method picks',
    body: 'Make members call how it ends (KO/TKO, submission or decision) on the main card, main plus prelims, or the whole card.',
    opts: [{ t: 'KO/TKO', hot: true }, { t: 'SUB' }, { t: 'DEC' }],
  },
  {
    title: 'Title-fight bonus',
    body: 'Belts on the line score more. Title fights multiply their points by your pool’s multiplier, ×1.5 by default.',
    opts: [{ t: '×1.5', gold: true }, { t: '×2' }, { t: 'you pick' }],
  },
  {
    title: 'Scoring that adds up',
    body: 'Main-card winners are worth the most, the undercard still counts, and nailing the method earns a bonus on top.',
    opts: [{ t: '5 · 3 · 1' }, { t: '+3 method', hot: true }],
  },
];

const SCORES = [
  { pts: '5', unit: 'pts', label: 'Main-card winner' },
  { pts: '3', unit: 'pts', label: 'Prelim winner' },
  { pts: '1', unit: 'pt', label: 'Early prelim winner' },
  { pts: '+3', unit: 'pts', label: 'Correct method' },
  { pts: '×1.5', unit: '', label: 'Title fights (default)', gold: true },
];

/** COMMISSIONER RULES: staggered glass cards with giant outline numerals + the scoring strip. */
export function RulesSection() {
  return (
    <section className="pnp-section" aria-labelledby="pnp-rules-title">
      <SectionLight bleed="60px">
        <div className="pnp-octagrid" style={{ opacity: 0.5 }} />
        <div className="pnp-glow" style={{ right: '-25%', top: '-5%', opacity: 0.7 }} />
        <div className="pnp-glow" style={{ left: '-30%', bottom: '-20%', top: 'auto', opacity: 0.5 }} />
      </SectionLight>
      <div className="pnp-wrap">
        <div className="pnp-head">
          <Eyebrow>Commissioner&apos;s rules</Eyebrow>
          <div id="pnp-rules-title"><KineticHeadline text={'Your pool.\nYour *rules.*'} /></div>
          <Reveal delay={200}>
            <p className="pnp-lead">Whoever creates a private pool runs it. Set the card, the method picks and the title-fight stakes before the first bell.</p>
          </Reveal>
        </div>

        <ol className="pnp-rules">
          {RULES.map((r, i) => (
            <li key={r.title}>
              <Reveal delay={(i % 4) * 110} style={{ height: '100%' }}>
                <Tilt>
                  <div className="pnp-glass pnp-glass-hover pnp-rule">
                    <Parallax speed={-0.05} max={20} className="pnp-rule-num-wrap">
                      <span className="pnp-outline-num pnp-rule-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                    </Parallax>
                    <h3 className="pnp-h4">{r.title}</h3>
                    <p className="pnp-body">{r.body}</p>
                    <div className="pnp-rule-opts">
                      {r.opts.map((o) => (
                        <span key={o.t} className={`pnp-rule-opt${o.hot ? ' is-hot' : ''}${o.gold ? ' is-gold' : ''}`}>{o.t}</span>
                      ))}
                    </div>
                  </div>
                </Tilt>
              </Reveal>
            </li>
          ))}
        </ol>

        <Reveal className="pnp-scoring" aria-label="Default scoring">
          {SCORES.map((s) => (
            <div key={s.label} className="pnp-score">
              <span className={`pnp-score-pts${s.gold ? ' pnp-gold' : ''}`}>{s.pts}{s.unit ? <small>{s.unit}</small> : null}</span>
              <span className="pnp-stat-label">{s.label}</span>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

const BADGES = [
  { src: '/images/title-belt.svg', label: 'Title Shot Winner' },
  { src: '/images/perfect-card.svg', label: 'Perfect Card' },
  { src: '/images/picks-made-100.svg', label: '100 Picks' },
  { src: '/images/events-picked-10.svg', label: '10 Events' },
  { src: '/images/commissioner-gold.svg', label: 'Commissioner 25+' },
  { src: '/images/perfect-method-main.svg', label: 'Perfect Method' },
];

/** BADGES & BELTS: belt gold, the badges loop, real badge art floating. */
export function BadgesSection() {
  return (
    <section className="pnp-section" aria-labelledby="pnp-badges-title">
      <SectionLight bleed="80px">
        <div className="pnp-glow pnp-glow-gold" style={{ right: '-20%', top: '5%', width: 1100, height: 1100 }} />
      </SectionLight>
      <div className="pnp-wrap pnp-split is-flip">
        <Reveal className="pnp-split-media pnp-scroll-scale" rise={false}>
          <LoopVideo name="feature-badges" feather="ui" label="Badges and the Title Shot belt in the Pick 'N Pray app" />
          <Claws tone="gold" className="pnp-split-claws" style={{ '--claw-w': '7px', '--claw-gap': '18px', left: '10%', top: '6%', height: '66%', opacity: 0.55 }} />
        </Reveal>
        <div className="pnp-split-copy">
          <Eyebrow tone="gold">Badges &amp; belts</Eyebrow>
          <div id="pnp-badges-title"><KineticHeadline text={'Earn the\n^gold.^'} /></div>
          <Reveal delay={200}>
            <p className="pnp-lead">
              Badges for every milestone: picks made, events played, perfect cards and perfect methods, and commissioner
              ranks from bronze to diamond. Finish #1 in the Title Shot pool and the belt goes on your profile.
            </p>
          </Reveal>
          <Reveal delay={320} className="pnp-badge-row">
            {BADGES.map((b) => (
              <span key={b.src} className="pnp-badge-tile pnp-float" title={b.label}>
                <img src={b.src} alt={b.label} loading="lazy" />
              </span>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
